import * as React from 'react'
import { FC, useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Formik, FormikErrors } from 'formik'
import { toast } from '@sales-center/components'

import { EditAdditionalInfoFormFields } from './EditFormFields'
import { EditAdditionalInfoFormModel } from './typings'
import { ENTITY_TYPES } from '@sales-center/common'
import { ITargetPest } from '@user/store/targetPests/typings'
import { selectAllTargetPests } from '@user/store/targetPests/slice'
import { selectLeadDetail } from '@user/store/lead/slice'
import { selectOpportunityDetail } from '@user/store/opportunity/slice'
import { useAppDispatch, useAppSelector } from '@user/store/hooks'
import {
  getLeadDetail,
  getLeadList,
  updateLeadInfo,
} from '@user/store/lead/api'
import {
  getOpportunities,
  getOpportunityDetail,
  updateOpportunityInfo,
} from '@user/store/opportunity/api'
import { getLeadNotes } from '@user/store/leadNotes/api'

import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { useNavigate } from 'react-router-dom'
import { APIValidationError } from '@user/store/services/crud'
import {
  customValuesToFormModel,
  customValuesToInputModel,
  validateCustomFields,
} from '@user/components/CustomFields'
import { useSelectCustomFieldData } from '@user/store/customFields'
import {
  entityCampaignId,
  entityLeadSourceId,
  entityTargetPests,
  useGetEntityFormDataOnMount,
} from '@user/components/Settings/EntityFormSettings'
import { useData } from './useData'

interface Props {
  entityId: number
  entityType: ENTITY_TYPES
  onClose: () => void
}

export const EditAdditionalInfoFormWrapper: FC<Props> = (props) => {
  const { entityId, entityType, onClose } = props
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [initialValues, setInitialValues] =
    useState<EditAdditionalInfoFormModel>()

  const loading = useData(props)

  const allTargetPests = useSelector(selectAllTargetPests)
  const { customFields } = useSelectCustomFieldData()

  const detail = useAppSelector((state) =>
    entityType === ENTITY_TYPES.Lead
      ? selectLeadDetail(state)
      : selectOpportunityDetail(state),
  )

  const { requiredEntitySettings, customFieldSettings } =
    useGetEntityFormDataOnMount(entityType)

  const validate = useCallback(
    (model: EditAdditionalInfoFormModel) => {
      let errors: FormikErrors<EditAdditionalInfoFormModel> = {}

      const {
        salesTeamId,
        salesFunnelId,
        assigneeId,
        targetPests,
        campaignId,
        leadSourceId,
        salesFunnelStageId,
      } = model

      // ownership info
      if (salesTeamId == 0) errors.salesTeamId = 'Required'
      if (salesFunnelId == 0) errors.salesFunnelId = 'Required'
      if (assigneeId == null) errors.assigneeId = 'Required'

      // attribution
      if (salesFunnelStageId == 0) errors.salesFunnelStageId = 'Required'
      if (
        targetPests.length == 0 &&
        requiredEntitySettings.find((x) => x.fieldId == entityTargetPests)
          ?.required
      )
        errors.targetPests = 'Required'
      if (
        (leadSourceId == null || leadSourceId === '') &&
        requiredEntitySettings.find((x) => x.fieldId == entityLeadSourceId)
          ?.required
      )
        errors.leadSourceId = 'Required'
      if (
        (campaignId == null || campaignId == '') &&
        requiredEntitySettings.find((x) => x.fieldId == entityCampaignId)
          ?.required
      )
        errors.campaignId = 'Required'

      errors = {
        ...errors,
        ...validateCustomFields(model, customFieldSettings),
      }

      return errors
    },
    [requiredEntitySettings, customFieldSettings],
  )

  const onSubmit = useCallback(
    async (model: EditAdditionalInfoFormModel) => {
      const targetPestInput: ITargetPest[] = []

      if (model.targetPests && model.targetPests.length) {
        model.targetPests.map((targetPestId) => {
          const detail = allTargetPests.find((t) => t.id == targetPestId)
          if (detail) targetPestInput.push(detail)
        })
      }

      const transformedModel = {
        ...model,
        assigneeId: model.assigneeId == 'unassigned' ? null : model.assigneeId,
        targetPests: targetPestInput,
        customFieldValues: customValuesToInputModel(model, customFields),
      }

      if (entityType == ENTITY_TYPES.Lead) {
        const response = await dispatch(updateLeadInfo(transformedModel))
        const updateDetail = await dispatch(getLeadDetail(entityId))
        const updateNotes = await dispatch(getLeadNotes({ leadId: entityId }))

        if (
          updateLeadInfo.fulfilled.match(response) &&
          getLeadDetail.fulfilled.match(updateDetail) &&
          getLeadNotes.fulfilled.match(updateNotes)
        ) {
          toast.success('Lead information updated.')
          // Display the optional toast message if any Draft agreements were updated
          if (response.payload.draftAgreementsUpdated) {
            toast.success(
              'Information has been updated on any draft agreements.',
            )
          }
          onClose()
        } else if (getLeadDetail.rejected.match(updateDetail)) {
          const { payload } = updateDetail
          if (payload) {
            if (
              payload.StatusCode === 403 ||
              (payload.Message === 'Permission denied' &&
                payload.StatusCode === 500)
            ) {
              dispatch(getLeadList())
              navigate('/leads')
            }
          }
        } else {
          toast.error('Failed to update lead information.')
        }
      }

      if (entityType == ENTITY_TYPES.Opportunity) {
        const response = await dispatch(updateOpportunityInfo(transformedModel))
        const updateDetail = await dispatch(getOpportunityDetail(entityId))

        if (
          updateOpportunityInfo.fulfilled.match(response) &&
          getOpportunityDetail.fulfilled.match(updateDetail)
        ) {
          toast.success('Opportunity information updated.')
          // Display the optional toast message if any Draft agreements were updated
          if (response.payload.draftAgreementsUpdated) {
            toast.success(
              'Information has been updated on any draft agreements.',
            )
          }
          onClose()
        } else if (getOpportunityDetail.rejected.match(updateDetail)) {
          const { payload } = updateDetail
          if (payload) {
            if (
              payload.StatusCode === 403 ||
              (payload.Message === 'Permission denied' &&
                payload.StatusCode === 500)
            ) {
              dispatch(getOpportunities())
              navigate('/opportunities')
            }
          }
        } else {
          const errorsResult = response.payload as APIValidationError
          toast.error(
            errorsResult
              ? errorsResult.errors![''].join('\n')
              : 'Failed to update opportunity information.',
          )
        }
      }
    },
    [
      customFields,
      entityType,
      allTargetPests,
      dispatch,
      entityId,
      onClose,
      navigate,
    ],
  )

  useEffect(() => {
    if (!detail || loading) return

    // Convert target pests from array of names to array of ids
    const targetPestInput: string[] = []
    if (detail.targetPests && detail.targetPests.length) {
      detail.targetPests.map((targetPestName) => {
        const detail = allTargetPests.find((t) => t.name == targetPestName)
        if (detail) targetPestInput.push(detail.id.toString())
      })
    }

    const customFieldValues = customValuesToFormModel(
      customFields,
      detail.customFieldValues,
    )

    setInitialValues({
      assigneeId: detail.assigneeId || 'unassigned',
      campaignId:
        !detail.campaignId || detail.campaignId == '0' ? '' : detail.campaignId,
      campaign: detail.campaign,
      id: detail.id,
      leadSourceId:
        !detail.leadSourceId || detail.leadSourceId == '0'
          ? ''
          : detail.leadSourceId,
      leadSource: detail.leadSource,
      locationTypeId: detail.locationTypeId || '',
      salesFunnelId: detail.salesFunnelId,
      salesFunnelStageId: detail.salesFunnelStageId,
      salesTeamId: detail.salesTeamId,
      targetPests: targetPestInput || [],
      branchId: detail.branchId,
      customFields: customFieldValues,
      removeServices: false,
    })
  }, [loading, allTargetPests, detail, customFields])

  return (
    <>
      {loading && (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress />
        </Box>
      )}

      {!loading && initialValues && (
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validate={validate}
        >
          <EditAdditionalInfoFormFields
            onCancel={onClose}
            entityType={entityType}
            salesFunnelInitialId={detail?.salesFunnelId}
            entityId={entityId}
          />
        </Formik>
      )}
    </>
  )
}

EditAdditionalInfoFormWrapper.displayName = 'EditAdditionalInfoFormWrapper'
